/**
 * `notificationRulesApi` — standalone RTK Query slice for the proxy-server
 * notification-rules surface (Design Doc 2 / AXI-4787): condition-driven
 * rules that email and/or Teams-notify operators when a shared trigger
 * fires.
 *
 * Split off from `ontologyApi` so the Configure → Notification Rules pages
 * own their own cache + tag space (`'NotificationRule'`) instead of being
 * lumped in with every other graph/ingest/plant query.
 * The slice shares `ontologyBaseQuery` (auth + branch headers, upstream URL)
 * and the `PROXY_API_V1_URL` constant with `ontologyApi` via
 * `ontology-api-base.ts`.
 *
 * Cross-slice invalidation: `sendTestNotificationRule` additionally
 * invalidates `'Notification'` on `notificationsApi` via
 * `notification-rules-notifications-bridge.ts` (listener middleware) since a
 * test send can create/update a notification row.
 */

import { createApi } from '@reduxjs/toolkit/query/react'
import { triggersToProxyWire } from '@atbash-labs/ontology-agent-wire'

import { PROXY_API_V1_URL, ontologyBaseQuery } from '../core'
import {
  denormalizeCreateNotificationRulePayload,
  denormalizeUpdateNotificationRulePayload,
  normalizeNotificationRule,
  normalizeNotificationRuleList
} from './notification-rule-transform'
import type { TagReading } from '../tags'
import {
  normalizeTriggerStateSnapshot,
  type TriggerEvalResult,
  type TriggerStateSnapshot
} from '../triggers'
import type { NotificationRuleEvaluation } from './conditions'
import type {
  CreateNotificationRulePayload,
  NotificationRule,
  NotificationRuleListPage,
  NotificationRuleMessagePreview,
  NotificationTemplateVariableCatalog,
  RenderNotificationRulePreviewPayload,
  SendTestNotificationResponse,
  UpdateNotificationRulePayload
} from './types'

export const NOTIFICATION_RULE_TAG_TYPES = ['NotificationRule'] as const
export type NotificationRuleTagType = (typeof NOTIFICATION_RULE_TAG_TYPES)[number]

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : []
}

/**
 * Wire shape of a row from the proxy's notification-rules evaluate endpoint:
 * exactly one of `value_*` is populated when `is_good` is true, and
 * `tag_type` picks which one. Identical shape to RCA's `BackendTagReading` —
 * kept local (this slice's only consumer) rather than promoted to `../tags`.
 */
interface BackendTagReading {
  time: string
  gateway_url?: string
  project?: string
  target_id?: string
  node_id?: string
  tag_path: string
  tag_type?: string
  value_double?: number | null
  value_int?: number | null
  value_bool?: boolean | null
  value_string?: string | null
  value_timestamp?: string | null
  value_json?: unknown
  quality?: string
  is_good?: boolean
}

function normalizeTagReading(response: BackendTagReading): TagReading {
  const valueDouble = response.value_double ?? null
  const valueInt = response.value_int ?? null
  const numeric = valueDouble !== null ? valueDouble : valueInt !== null ? valueInt : null
  return {
    time: response.time,
    gatewayUrl: response.gateway_url ?? '',
    project: response.project ?? '',
    targetId: response.target_id,
    nodeId: response.node_id ?? '',
    tagPath: response.tag_path,
    tagType: response.tag_type ?? '',
    valueDouble,
    valueInt,
    valueBool: response.value_bool ?? null,
    valueString: response.value_string ?? null,
    valueTimestamp: response.value_timestamp ?? null,
    valueJson: response.value_json ?? null,
    quality: response.quality ?? 'Good',
    isGood: response.is_good ?? true,
    value: numeric
  }
}

export interface ListNotificationRulesParams {
  state?: 'active' | 'disabled'
  q?: string
  page_size?: number
  page_token?: string
}

export const notificationRulesApi = createApi({
  reducerPath: 'notificationRulesApi',
  baseQuery: ontologyBaseQuery,
  refetchOnFocus: true,
  refetchOnReconnect: true,
  tagTypes: NOTIFICATION_RULE_TAG_TYPES,
  endpoints: (builder) => ({
    listNotificationRules: builder.query<NotificationRule[], ListNotificationRulesParams | void>({
      query: (params) => ({
        url: `${PROXY_API_V1_URL}/notification-rules`,
        params: params ?? undefined
      }),
      transformResponse: (response: NotificationRuleListPage | null) =>
        normalizeNotificationRuleList(asArray<NotificationRule>(response?.notification_rules)),
      providesTags: (result) =>
        result
          ? [
              ...result.map((r) => ({
                type: 'NotificationRule' as const,
                id: r.id
              })),
              { type: 'NotificationRule' as const, id: 'LIST' }
            ]
          : [{ type: 'NotificationRule' as const, id: 'LIST' }]
    }),
    getNotificationRule: builder.query<NotificationRule, string>({
      query: (id) => `${PROXY_API_V1_URL}/notification-rules/${id}`,
      transformResponse: (response: NotificationRule) => normalizeNotificationRule(response),
      providesTags: (_r, _e, id) => [{ type: 'NotificationRule', id }]
    }),
    getNotificationRuleTemplateVariables: builder.query<NotificationTemplateVariableCatalog, void>({
      query: () => `${PROXY_API_V1_URL}/notification-rules:templateVariables`
    }),
    renderNotificationRulePreview: builder.mutation<
      NotificationRuleMessagePreview,
      RenderNotificationRulePreviewPayload
    >({
      query: (body) => ({
        url: `${PROXY_API_V1_URL}/notification-rules:renderPreview`,
        method: 'POST',
        body: {
          ...body,
          triggers: body.triggers ? triggersToProxyWire(body.triggers) : undefined
        }
      })
    }),
    // Server-evaluated trigger state for a notification rule. Runs the same
    // shared evaluator RCA/PM use and returns a per-node transcript — state +
    // current/agg/baseline/metric values — so the detail page can render
    // fired/not_fired/unknown badges per trigger. The response also bundles
    // the readings the evaluator saw, which double as the leaf `tag_path`
    // source for equipment-scope display.
    getNotificationRuleEvaluation: builder.query<
      NotificationRuleEvaluation,
      { notificationRuleId: string }
    >({
      query: ({ notificationRuleId }) =>
        `${PROXY_API_V1_URL}/notification-rules/${notificationRuleId}/evaluate`,
      transformResponse: (response: {
        notification_rule_id: string
        evaluated_at: string
        gateway_url?: string
        project?: string
        readings?: BackendTagReading[]
        triggers?: TriggerEvalResult[]
      }): NotificationRuleEvaluation => ({
        notificationRuleId: response.notification_rule_id,
        evaluatedAt: response.evaluated_at,
        gatewayUrl: response.gateway_url ?? '',
        project: response.project ?? '',
        readings: asArray<BackendTagReading>(response.readings).map(normalizeTagReading),
        triggers: asArray<TriggerEvalResult>(response.triggers)
      }),
      providesTags: (_r, _e, { notificationRuleId }) => [
        { type: 'NotificationRule', id: notificationRuleId }
      ]
    }),
    getNotificationRuleTriggerState: builder.query<
      TriggerStateSnapshot,
      { notificationRuleId: string }
    >({
      query: ({ notificationRuleId }) =>
        `${PROXY_API_V1_URL}/notification-rules/${notificationRuleId}/trigger-state`,
      transformResponse: (response: unknown, _meta, { notificationRuleId }) =>
        normalizeTriggerStateSnapshot(response, notificationRuleId),
      providesTags: (_r, _e, { notificationRuleId }) => [
        { type: 'NotificationRule', id: notificationRuleId }
      ]
    }),
    updateNotificationRuleTriggerOccurrence: builder.mutation<
      void,
      {
        notificationRuleId: string
        occurrenceId: string
        action: 'acknowledge' | 'suppress' | 'unsuppress'
      }
    >({
      query: ({ notificationRuleId, occurrenceId, action }) => ({
        url: `${PROXY_API_V1_URL}/notification-rules/${encodeURIComponent(notificationRuleId)}/trigger-occurrences/${encodeURIComponent(occurrenceId)}:${action}`,
        method: 'POST'
      }),
      invalidatesTags: (_r, _e, { notificationRuleId }) => [
        { type: 'NotificationRule', id: notificationRuleId }
      ]
    }),
    createNotificationRule: builder.mutation<NotificationRule, CreateNotificationRulePayload>({
      query: (body) => ({
        url: `${PROXY_API_V1_URL}/notification-rules`,
        method: 'POST',
        body: denormalizeCreateNotificationRulePayload(body)
      }),
      transformResponse: (response: NotificationRule) => normalizeNotificationRule(response),
      invalidatesTags: [{ type: 'NotificationRule', id: 'LIST' }]
    }),
    updateNotificationRule: builder.mutation<
      NotificationRule,
      { id: string; patch: UpdateNotificationRulePayload }
    >({
      query: ({ id, patch }) => ({
        url: `${PROXY_API_V1_URL}/notification-rules/${id}`,
        method: 'PATCH',
        body: denormalizeUpdateNotificationRulePayload(patch)
      }),
      transformResponse: (response: NotificationRule) => normalizeNotificationRule(response),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'NotificationRule', id },
        { type: 'NotificationRule', id: 'LIST' }
      ]
    }),
    deleteNotificationRule: builder.mutation<void, string>({
      query: (id) => ({
        url: `${PROXY_API_V1_URL}/notification-rules/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'NotificationRule', id },
        { type: 'NotificationRule', id: 'LIST' }
      ]
    }),
    // Delivers immediately, ignoring the rule's state and cooldown — surfaces
    // delivery failures directly (unlike a real trigger fire, which logs and
    // swallows them). Invalidates the rule itself so a bumped
    // `last_notified_at` — the proxy's SendTest bypasses the cooldown gate but
    // still records the send — refreshes on the detail page; also invalidates
    // `'Notification'` on `notificationsApi` via
    // `notification-rules-notifications-bridge.ts`.
    sendTestNotificationRule: builder.mutation<SendTestNotificationResponse, string>({
      query: (id) => ({
        url: `${PROXY_API_V1_URL}/notification-rules/${id}:sendTest`,
        method: 'POST'
      }),
      invalidatesTags: (_r, _e, id) => [{ type: 'NotificationRule', id }]
    })
  })
})

export const {
  useListNotificationRulesQuery,
  useGetNotificationRuleQuery,
  useGetNotificationRuleTemplateVariablesQuery,
  useRenderNotificationRulePreviewMutation,
  useGetNotificationRuleEvaluationQuery,
  useGetNotificationRuleTriggerStateQuery,
  useUpdateNotificationRuleTriggerOccurrenceMutation,
  useCreateNotificationRuleMutation,
  useUpdateNotificationRuleMutation,
  useDeleteNotificationRuleMutation,
  useSendTestNotificationRuleMutation
} = notificationRulesApi
